import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  useToastController,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useAuthStore } from '../stores/authStore.js';
import { GLOBAL_TOASTER_ID } from './ThemedProvider.js';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';

const useStyles = makeStyles({
  surface: { position: 'relative' },
  close: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    minWidth: '32px',
    height: '32px',
    padding: 0,
  },
  body: { display: 'flex', flexDirection: 'column', gap: 'var(--space-m)' },
  footnote: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-200)',
  },
});

const RENEW_URL = 'https://ecosoft.com.mx/#contacto';

/**
 * Gate global para 403 OPUS_SUBSCRIPTION_INACTIVE (sesión ya abierta).
 * En /login el bloqueo lo maneja LoginPage (misma UX que el hub).
 *
 * Hard block: Renovar + Cerrar (+ X). Sin "Continuar" ni cooldown.
 */
export function SubscriptionInactiveGate() {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const lastToastAt = useRef(0);
  const clearSession = useAuthStore((s) => s.clearSession);
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  useEffect(() => {
    function onInactive(ev: Event) {
      const detail = (ev as CustomEvent<{ code: string; message: string; details?: { readOnly?: boolean } } | undefined>).detail;
      console.warn('[OPUS_SUBSCRIPTION_INACTIVE]', detail);

      // Login tiene pantalla propia; no apilar modal encima del selector.
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/login')) {
        return;
      }

      if (detail?.details?.readOnly === true) {
        const now = Date.now();
        if (now - lastToastAt.current < 15_000) return;
        lastToastAt.current = now;
        dispatchToast(
          <Toast>
            <ToastTitle>Acción bloqueada — modo solo lectura</ToastTitle>
            <ToastBody>
              {detail.message ?? 'Tu suscripción OPUS 27 está vencida. Renueva para crear o modificar.'}
            </ToastBody>
          </Toast>,
          { intent: 'warning', timeout: 6_000 },
        );
        return;
      }
      setMessage(detail?.message ?? null);
      setOpen(true);
    }
    window.addEventListener('opus:subscription-inactive', onInactive);
    return () => window.removeEventListener('opus:subscription-inactive', onInactive);
  }, [dispatchToast]);

  async function onLogout() {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignorar — limpiamos local de todas formas.
    }
    clearSession();
    setOpen(false);
    window.location.href = '/login';
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) void onLogout(); }} modalType="modal">
      <DialogSurface className={styles.surface}>
        <Button
          className={styles.close}
          appearance="subtle"
          icon={<Dismiss24Regular />}
          aria-label="Cerrar"
          onClick={() => void onLogout()}
        />
        <DialogBody>
          <DialogTitle>Suscripción OPUS 27 inactiva</DialogTitle>
          <DialogContent className={styles.body}>
            <p>
              {message ?? `Tu acceso a ${nombreProductoActivo()} requiere una suscripción OPUS 27 activa.`}
            </p>
            <p className={styles.footnote}>
              Si acabas de renovar, cierra sesión y vuelve a entrar para que se
              actualice tu acceso.
            </p>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => void onLogout()}>
              Cerrar
            </Button>
            <Button
              appearance="primary"
              as="a"
              href={RENEW_URL}
              target="_blank"
              rel="noreferrer"
            >
              Renovar OPUS 27
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
